import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Button, Container, Alert, Row, Col, InputGroup, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { EyeFill, EyeSlashFill } from 'react-bootstrap-icons'
import { amazonAPI } from '../api/amazonAPI'
import { brandAPI } from '../api/brandAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'

function AmazonAccountCreateScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const navigate = useNavigate()
    const [error, setError] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const [availableBrands, setAvailableBrands] = useState([])
    const [allAccounts, setAllAccounts] = useState([])
    const [account, setAccount] = useState({
        account_name: '',
        brand_ids: [],
        lwa_client_id: '',
        lwa_client_secret: '',
        refresh_token: '',
    })
    const [showSecrets, setShowSecrets] = useState({
        lwa_client_id: false,
        lwa_client_secret: false,
        refresh_token: false,
    })

    useEffect(() => {
        async function fetchData() {
            try {
                const [brandsRes, accounts] = await Promise.all([
                    brandAPI.getAll(1, 1000, '', false),
                    amazonAPI.accounts.getAll(),
                ])
                setAvailableBrands(brandsRes.results || [])
                setAllAccounts(accounts || [])
            } catch (error) {
                console.error('Error fetching brands/accounts:', error)
            }
        }
        fetchData()
    }, [])

    const brandToAccount = {}
    allAccounts.forEach(acc => {
        (acc.brand_ids || []).forEach(brandId => {
            brandToAccount[brandId] = acc.account_name
        })
    })

    const toggleBrand = (brandId) => {
        const current = account.brand_ids
        if (current.includes(brandId)) {
            setAccount({ ...account, brand_ids: current.filter(id => id !== brandId) })
        } else {
            setAccount({ ...account, brand_ids: [...current, brandId] })
        }
    }

    const submitHandler = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        try {
            await amazonAPI.accounts.create(account)
            navigate('/amazon-accounts')
        } catch (error) {
            setError(formatErrorResponse(error))
        } finally {
            setSubmitting(false)
        }
    }

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!hasPermission('amazon.manage')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to manage Amazon accounts.
                </Alert>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
                <Col style={{ maxWidth: '730px' }}>
                    <h1>Create Amazon Account</h1>
                    <Form onSubmit={submitHandler}>
                        <Form.Group className="mb-4" controlId="account_name">
                            <Form.Label>Account Name*</Form.Label>
                            <Form.Control
                                type="text"
                                value={account.account_name}
                                onChange={(e) => setAccount({ ...account, account_name: e.target.value })}
                                required
                                placeholder="Enter account name"
                                autoComplete="one-time-code"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="brands">
                            <Form.Label>Brands</Form.Label>
                            <div className="d-flex flex-wrap gap-2 p-3 border rounded">
                                {availableBrands.map((brand) => {
                                    const assignedTo = brandToAccount[brand._id]
                                    const isSelected = account.brand_ids.includes(brand._id)
                                    if (assignedTo) {
                                        return (
                                            <OverlayTrigger
                                                key={brand._id}
                                                overlay={<Tooltip>Assigned to {assignedTo}</Tooltip>}
                                            >
                                                <Badge bg="secondary" style={{ cursor: 'not-allowed', opacity: 0.5 }}>
                                                    {brand.name}
                                                </Badge>
                                            </OverlayTrigger>
                                        )
                                    }
                                    return (
                                        <Badge
                                            key={brand._id}
                                            bg={isSelected ? 'success' : 'light'}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => toggleBrand(brand._id)}
                                        >
                                            {brand.name}
                                        </Badge>
                                    )
                                })}
                            </div>
                            <Form.Text className="text-muted">
                                Select brands sold on this Amazon account
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="lwa_client_id">
                            <Form.Label>LWA Client ID*</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showSecrets.lwa_client_id ? "text" : "password"}
                                    value={account.lwa_client_id}
                                    onChange={(e) => setAccount({ ...account, lwa_client_id: e.target.value })}
                                    required
                                    placeholder="Enter LWA Client ID"
                                    autoComplete="one-time-code"
                                />
                                <Button
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowSecrets({ ...showSecrets, lwa_client_id: !showSecrets.lwa_client_id })}
                                >
                                    {showSecrets.lwa_client_id ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="lwa_client_secret">
                            <Form.Label>LWA Client Secret*</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showSecrets.lwa_client_secret ? "text" : "password"}
                                    value={account.lwa_client_secret}
                                    onChange={(e) => setAccount({ ...account, lwa_client_secret: e.target.value })}
                                    required
                                    placeholder="Enter LWA Client Secret"
                                    autoComplete="one-time-code"
                                />
                                <Button
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowSecrets({ ...showSecrets, lwa_client_secret: !showSecrets.lwa_client_secret })}
                                >
                                    {showSecrets.lwa_client_secret ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="refresh_token">
                            <Form.Label>Refresh Token*</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showSecrets.refresh_token ? "text" : "password"}
                                    value={account.refresh_token}
                                    onChange={(e) => setAccount({ ...account, refresh_token: e.target.value })}
                                    required
                                    placeholder="Enter Refresh Token"
                                    autoComplete="one-time-code"
                                />
                                <Button
                                    variant="outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => setShowSecrets({ ...showSecrets, refresh_token: !showSecrets.refresh_token })}
                                >
                                    {showSecrets.refresh_token ? <EyeSlashFill /> : <EyeFill />}
                                </Button>
                            </InputGroup>
                        </Form.Group>

                        {error && (
                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                {error}
                            </Alert>
                        )}
                        <Button variant="success" type="submit" className="me-2" disabled={submitting}>
                            {submitting ? 'Creating...' : 'Create'}
                        </Button>
                        <Button
                            variant="outline-primary"
                            onClick={() => navigate('/amazon-accounts')}
                            type="button"
                        >
                            <i className="fas fa-arrow-left me-2"></i>
                            Back to List
                        </Button>
                    </Form>
                </Col>
            </Row>
        </Container>
    )
}

export default AmazonAccountCreateScreen
